import { useEffect, useState } from "react";
import { Building2, Loader2, Plus, Star, Trash2 } from "lucide-react";
import { AccountBadges } from "@/components/AccountBadges";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const FIELDS = [
  { k: "account_holder", l: "Account holder name" },
  { k: "bank_name", l: "Bank name" },
  { k: "account_number", l: "Account number / IBAN" },
];
const EMPTY = { account_holder: "", bank_name: "", account_number: "" };
const inputCls = "w-full rounded-xl border border-[#212638] bg-[#0F111A] px-3 py-2.5 text-sm outline-none transition-colors focus:border-[#F7931A]";

export const BankAccounts = () => {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const valid = form.account_holder.trim().length >= 2 && form.bank_name.trim().length >= 2 && form.account_number.replace(/\s/g, "").length >= 4;

  const load = () => api.get("/bank-accounts").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const add = async () => {
    setBusy(true);
    try {
      await api.post("/bank-accounts", form);
      toast.success("Bank account saved");
      setForm(EMPTY);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const makeDefault = (id) => api.post(`/bank-accounts/${id}/default`).then(() => { toast.success("Default account updated"); load(); }).catch((e) => toast.error(errMsg(e)));
  const remove = (id) => api.delete(`/bank-accounts/${id}`).then(() => { toast.success("Bank account removed"); load(); }).catch((e) => toast.error(errMsg(e)));

  return (
    <div className="space-y-5" data-testid="bank-accounts-panel">
      <p className="text-sm text-[#94A3B8]">Saved accounts appear in the withdraw window so you don't have to retype them. Star one as your default. After your first confirmed withdrawal to an account it becomes trusted, and future withdrawals to it skip the email code. Only the last 4 digits are shown.</p>
      <div className="space-y-2">
        {!items ? <p className="py-4 text-center text-xs text-[#64748B]">Loading...</p> : items.length === 0 ? (
          <p className="rounded-xl border border-dashed border-[#212638] py-6 text-center text-sm text-[#64748B]" data-testid="bank-accounts-empty">No saved bank accounts yet.</p>
        ) : items.map((a) => (
          <div key={a.account_id} className="flex items-center gap-3 rounded-xl border border-[#212638] bg-[#0F111A] px-4 py-3" data-testid={`bank-account-${a.account_id}`}>
            <Building2 size={18} className="text-[#F7931A]" />
            <div className="flex-1">
              <p className="text-sm font-semibold">{a.bank_name} <span className="font-mono text-[#94A3B8]">••••{a.last4}</span></p>
              <p className="text-xs text-[#94A3B8]">{a.account_holder}</p>
            </div>
            <AccountBadges a={a} />
            <button onClick={() => !a.is_default && makeDefault(a.account_id)} title={a.is_default ? "Default account" : "Make default"} data-testid={`bank-account-default-${a.account_id}`}
              className={`rounded-lg p-2 transition-colors hover:bg-[#1C2030] ${a.is_default ? "text-[#F7931A]" : "text-[#64748B] hover:text-[#F7931A]"}`}>
              <Star size={15} fill={a.is_default ? "currentColor" : "none"} />
            </button>
            <button onClick={() => remove(a.account_id)} title="Remove" data-testid={`bank-account-delete-${a.account_id}`}
              className="rounded-lg p-2 text-[#94A3B8] transition-colors hover:bg-red-500/10 hover:text-red-400"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
      <div className="space-y-2 rounded-xl border border-[#212638] p-4">
        <p className="text-sm font-semibold">Add a bank account</p>
        {FIELDS.map((f) => (
          <input key={f.k} value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} placeholder={f.l} aria-label={f.l}
            data-testid={`bank-${f.k.replace(/_/g, "-")}-input`} className={inputCls} />
        ))}
        <button onClick={add} disabled={!valid || busy} data-testid="bank-account-add-button"
          className="flex items-center gap-2 rounded-xl bg-[#F7931A] px-4 py-2.5 text-sm font-semibold text-[#08090E] transition-colors hover:bg-amber-500 disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Save account
        </button>
      </div>
    </div>
  );
};
